import { useCallback, useState } from 'react'

/**
 * Interface for options that can be passed to the useDownload hook.
 * @property {boolean} [redirectOnError=true] - Whether to open the file URL in a new tab if the download fails.
 */
interface UseDownloadOptions {
  redirectOnError?: boolean
}

/**
 * Interface for the return values of the useDownload hook.
 * @property {(url: string, filename: string) => Promise<void>} download - The function to trigger the download.
 * @property {boolean} isLoading - Indicates whether a download is currently in progress.
 * @property {string | null} error - Contains an error message if the download fails and redirectOnError is false.
 */

interface UseDownloadReturn {
  download: (url: string, filename: string) => Promise<void>
  isLoading: boolean
  error: string | null
}

/**
 * A React hook for downloading files with support for handling errors and optionally redirecting
 * to the file's URL if a download error occurs.
 *
 * @param {UseDownloadOptions} [options] - Configuration options for the download behavior.
 * @param {boolean} [options.redirectOnError=true] - If true, opens the file in a new tab when a download error occurs. If false, sets an error message instead.
 * @returns {UseDownloadReturn} - An object containing the download function, isLoading state, and error state.
 *
 * @example
 * // Default usage: Redirect to the file URL on error.
 * const { download, isLoading, error } = useDownload();
 *
 * <button onClick={() => download('https://example.com/file.pdf', 'myfile.pdf')}>
 *   {isLoading ? 'Downloading...' : 'Download PDF'}
 * </button>
 * {error && <p>Error: {error}</p>}
 *
 * @example
 * // Custom usage: Show error message on download failure instead of redirecting.
 * const { download, isLoading, error } = useDownload({ redirectOnError: false });
 *
 * <button onClick={() => download('https://example.com/file.pdf', 'myfile.pdf')}>
 *   {isLoading ? 'Downloading...' : 'Download PDF'}
 * </button>
 * {error && <p>Error: {error}</p>}
 */
const useDownload = ({
  redirectOnError = true
}: UseDownloadOptions = {}): UseDownloadReturn => {
  // State to track if the download is in progress
  const [isLoading, setIsLoading] = useState(false)

  // State to store any errors that occur during the download
  const [error, setError] = useState<string | null>(null)

  /**
   * Triggers the download of a file from the given URL.
   * If `redirectOnError` is true and the download fails, it opens the file URL in a new tab.
   *
   * @param {string} url - The URL of the file to download.
   * @param {string} filename - The name of the file to be saved on the user's device.
   */
  const download = useCallback(
    async (url: string, filename: string) => {
      setIsLoading(true)
      setError(null)

      try {
        // Fetch the file from the server
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`)
        }

        // Create a Blob from the response to enable downloading
        const blob = await response.blob()

        // Create a temporary link element to download the Blob
        const link = document.createElement('a')
        const blobUrl = URL.createObjectURL(blob)
        link.href = blobUrl
        link.setAttribute('download', filename) // Specify the download filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link) // Clean up the DOM
        URL.revokeObjectURL(blobUrl) // Revoke the Blob URL after download
      } catch (err) {
        console.error('Error during download', err)
        // If redirectOnError is true, open the file URL in a new tab on error
        if (redirectOnError) {
          window.open(url, '_blank')
        } else {
          // Set the error message if redirectOnError is false
          setError('Error during download')
        }
      } finally {
        setIsLoading(false) // Reset the loading state
      }
    },
    [redirectOnError]
  )

  return {
    download, // The download function to trigger the file download
    isLoading, // The loading state indicating whether a download is in progress
    error // The error state containing the error message if download fails
  }
}

export default useDownload
